/**
 * Fireballs (vanilla LargeFireball / SmallFireball). DOM-free.
 *  - large (ghast): flies straight with a constant 0.1 acceleration toward its
 *    aim (vanilla power), 6 damage on a direct hit, then an explosion of power 1
 *    that lights fires. A player hit can deflect it (it reverses toward the
 *    player's look direction and then hurts ghasts).
 *  - small (blaze): 5 damage, sets the target on fire for 5 s, lights a fire
 *    block where it lands, no explosion.
 */

import { AIR, FIRE_ID, IS_SOLID, SOUL_FIRE_ID, SOUL_SAND_ID, SOUL_SOIL_ID } from '../../blocks';
import type { World } from '../../world/chunk';
import type { Mob } from '../mob';

export interface Fireball {
  id: number;
  small: boolean;
  x: number; y: number; z: number;
  px: number; py: number; pz: number;
  /** Acceleration (vanilla xPower...). Velocity follows it with 0.95 drag. */
  ax: number; ay: number; az: number;
  vx: number; vy: number; vz: number;
  age: number;
  /** Shooter mob id; 0 = deflected by the player. */
  owner: number;
  removed: boolean;
}

export interface FireballHost {
  world: World;
  player: { x: number; y: number; z: number; width: number; height: number; alive: boolean };
  hurtPlayer(amount: number, fromX: number, fromY: number, fromZ: number, fireTicks: number): void;
  mobs(): readonly Mob[];
  hurtMob(m: Mob, amount: number, fromX: number, fromZ: number): void;
  /** Explosion (blocks + area damage), power 1 for a ghast fireball. */
  explode(x: number, y: number, z: number, power: number): void;
  canEdit(x: number, y: number, z: number): boolean;
  setBlock(x: number, y: number, z: number, id: number): void;
  rnd(): number;
}

const MAX_AGE = 600;

export class Fireballs {
  readonly list: Fireball[] = [];
  private nextId = 1;

  spawn(small: boolean, x: number, y: number, z: number, dx: number, dy: number, dz: number, owner: number, rnd: () => number): Fireball {
    // vanilla: aim plus gaussian spread, normalised, power 0.1
    const spread = small ? 0.1 : 0.0;
    const gx = dx + (rnd() - 0.5) * spread * Math.hypot(dx, dz);
    const gz = dz + (rnd() - 0.5) * spread * Math.hypot(dx, dz);
    const l = Math.hypot(gx, dy, gz) || 1;
    const f: Fireball = {
      id: this.nextId++, small, x, y, z, px: x, py: y, pz: z,
      ax: (gx / l) * 0.1, ay: (dy / l) * 0.1, az: (gz / l) * 0.1, vx: 0, vy: 0, vz: 0,
      age: 0, owner, removed: false,
    };
    this.list.push(f);
    return f;
  }

  clear(): void { this.list.length = 0; }

  /** Player punch: deflect the first fireball along the ray within reach. */
  deflect(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, reach: number): boolean {
    for (const f of this.list) {
      if (f.removed || f.small) continue;
      const rx = f.x - ox, ry = f.y - oy, rz = f.z - oz;
      const t = rx * dx + ry * dy + rz * dz;
      if (t < 0 || t > reach) continue;
      const cx = rx - dx * t, cy = ry - dy * t, cz = rz - dz * t;
      if (cx * cx + cy * cy + cz * cz > 0.75 * 0.75) continue;
      f.ax = dx * 0.1; f.ay = dy * 0.1; f.az = dz * 0.1;
      f.vx = dx * 0.6; f.vy = dy * 0.6; f.vz = dz * 0.6;
      f.owner = 0;
      return true;
    }
    return false;
  }

  tick(h: FireballHost): void {
    for (const f of this.list) {
      if (f.removed) continue;
      f.px = f.x; f.py = f.y; f.pz = f.z;
      if (++f.age > MAX_AGE) { f.removed = true; continue; }
      f.vx = (f.vx + f.ax) * 0.95; f.vy = (f.vy + f.ay) * 0.95; f.vz = (f.vz + f.az) * 0.95;
      // march in small steps so a fast ball never skips a wall or the player
      const steps = 4;
      for (let s = 0; s < steps && !f.removed; s++) {
        f.x += f.vx / steps; f.y += f.vy / steps; f.z += f.vz / steps;
        this.collide(f, h);
      }
    }
    let w = 0;
    for (const f of this.list) if (!f.removed) this.list[w++] = f;
    this.list.length = w;
  }

  private collide(f: Fireball, h: FireballHost): void {
    const r = f.small ? 0.16 : 0.5;
    const p = h.player;
    if (p.alive && f.age > 2 && f.owner !== 0 &&
      Math.abs(f.x - p.x) < p.width / 2 + r && Math.abs(f.z - p.z) < p.width / 2 + r && f.y > p.y - r && f.y < p.y + p.height + r) {
      h.hurtPlayer(f.small ? 5 : 6, f.x, f.y, f.z, f.small ? 100 : 0);
      this.impact(f, h, false);
      return;
    }
    for (const m of h.mobs()) {
      if (!m.alive || m.id === f.owner) continue;
      if (f.owner !== 0 && f.small) continue; // blazes don't hit each other
      if (Math.abs(f.x - m.x) < m.width / 2 + r && Math.abs(f.z - m.z) < m.width / 2 + r && f.y > m.y - r && f.y < m.y + m.height + r) {
        // a deflected ghast fireball one-shots a ghast (vanilla: 1000 damage)
        h.hurtMob(m, f.owner === 0 && m.type === 'ghast' ? 1000 : f.small ? 5 : 6, f.x, f.z);
        this.impact(f, h, false);
        return;
      }
    }
    const bx = Math.floor(f.x), by = Math.floor(f.y), bz = Math.floor(f.z);
    if (IS_SOLID[h.world.getBlock(bx, by, bz)] === 1) this.impact(f, h, true);
  }

  private impact(f: Fireball, h: FireballHost, block: boolean): void {
    f.removed = true;
    if (!f.small) {
      h.explode(f.x, f.y, f.z, 1);
      // ghast fireballs light fires around the crater (vanilla: 1/3 of air cells over solid)
      for (let i = 0; i < 12; i++) {
        const x = Math.floor(f.x + (h.rnd() - 0.5) * 4), y = Math.floor(f.y + (h.rnd() - 0.5) * 3), z = Math.floor(f.z + (h.rnd() - 0.5) * 4);
        this.light(h, x, y, z);
      }
    } else if (block) {
      // back up out of the wall and set the cell alight
      this.light(h, Math.floor(f.x - f.vx), Math.floor(f.y - f.vy), Math.floor(f.z - f.vz));
    }
  }

  private light(h: FireballHost, x: number, y: number, z: number): void {
    if (h.world.getBlock(x, y, z) !== AIR || !h.canEdit(x, y, z)) return;
    const below = h.world.getBlock(x, y - 1, z);
    if (IS_SOLID[below] !== 1) return;
    h.setBlock(x, y, z, below === SOUL_SAND_ID || below === SOUL_SOIL_ID ? SOUL_FIRE_ID : FIRE_ID);
  }
}
